import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import ActingBodiesUnitsDropdown from './ActingBodiesUnitsDropdown';
import moment from 'moment';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandsDropdown';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import { View } from 'react-native';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { UnitStatus, unitStatus } from '../../../schemas/dispatch';
import CategoriesDropdown from '../TeamNames/CategoriesDropdown';

interface Props {
    filters: DispatchParamList['Units'];
    setFilters: React.Dispatch<React.SetStateAction<DispatchParamList['Units']>>;
    authenticatedUser?: AuthenticatedUser | null;
    teamFilter: string;
    setTeamFilter: (value: string) => void;
}

function UnitsFilter({ authenticatedUser, filters, setFilters, teamFilter, setTeamFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DispatchParamList, 'Units'>>>();

    const [agentFilter, setAgentFilter] = useState(filters.agentFilter ?? '');
    const [status, setStatus] = useState<UnitStatus[]>(filters.status ?? []);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitIds, setActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [actingBodyCommandIds, setActingBodyCommandIds] = useState<number[]>(filters.actingBodyCommandIds ?? []);
    const [overdueUnits, setOverdueUnits] = useState<boolean>(filters.overdueUnits ?? false);
    const [categoryFilter, setCategoryFilter] = useState<number[]>([]);
    const [isConnected, setIsConnected] = useState<boolean>(filters.isConnected ?? false);
    const [agentOnline, setAgentOnline] = useState<boolean>(filters.agentOnline ?? false);
    const [agentOffline, setAgentOffline] = useState<boolean>(filters.agentOffline ?? false);

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const handleCleanFilter = () => {
        setAgentFilter('');
        setStatus([]);
        setTeamFilter('');
        setActingBodyId(0);
        setActingBodyUnitIds([]);
        setActingBodyCommandIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setOverdueUnits(false);
        setCategoryFilter([]);
        setIsConnected(false);
        setAgentOnline(false);
        setAgentOffline(false);
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        navigation.setParams({
            page: 1,
            status: status.length ? status : undefined,
            actingBodyId: actingBodyId > 0 ? actingBodyId : undefined,
            actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            agentFilter: agentFilter !== '' ? agentFilter : undefined,
            teamFilter: teamFilter !== '' ? teamFilter : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            overdueUnits: overdueUnits ? overdueUnits : undefined,
            teamCategoryId: categoryFilter.length > 0 ? categoryFilter : undefined,
            isConnected: isConnected == true ? isConnected : undefined,
            agentOnline: agentOnline == true ? agentOnline : undefined,
            agentOffline: agentOffline == true ? agentOffline : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            status: status.length ? status : undefined,
            actingBodyId: actingBodyId > 0 ? actingBodyId : undefined,
            actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            agentFilter: agentFilter !== '' ? agentFilter : undefined,
            teamFilter: teamFilter !== '' ? teamFilter : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            overdueUnits: overdueUnits ? overdueUnits : undefined,
            teamCategoryId: categoryFilter.length > 0 ? categoryFilter : undefined,
            isConnected: isConnected == true ? isConnected : undefined,
            agentOnline: agentOnline == true ? agentOnline : undefined,
            agentOffline: agentOffline == true ? agentOffline : undefined,
        });
    };

    const getActingBodyId = () => {
        if (authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units) {
            return actingBodyId;
        }
        return authenticatedUser?.actingBodyUnit.actingBodyId ?? 0;
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units) &&
                <ActingBodiesDropdown value={actingBodyId} setValue={setActingBodyId} includeAllOption={true} zIndex={7} disabled={false} />
            }

            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_acting_body_units) &&
                <ActingBodiesUnitsDropdown
                    value={actingBodyUnitIds}
                    actingBodyId={authenticatedUser?.isAdmin ? actingBodyId : authenticatedUser?.actingBodyUnit.actingBodyId ?? 0}
                    setValue={setActingBodyUnitIds}
                    zIndex={6}
                />
            }
            <ActingBodiesCommandsDropdown
                value={actingBodyCommandIds}
                actingBodyId={authenticatedUser?.isAdmin ? actingBodyId : authenticatedUser?.actingBodyUnit.actingBodyId ?? 0}
                setValue={setActingBodyCommandIds}
                zIndex={5}
            />

            <FilterInput
                label={translate('searchUnit')}
                value={teamFilter}
                placeholder={translate('typeToSearch')}
                onChange={text => {
                    setTeamFilter(text);
                }}
            />

            <FilterInput
                label={translate('searchAgent')}
                value={agentFilter}
                placeholder={translate('typeToSearch')}
                onChange={text => {
                    setAgentFilter(text);
                }}
            />

            <FilterDropdown
                label={translate('status')}
                items={unitStatus.map((item: string) => {
                    return {
                        label: item ? translate(item) : item, value: item
                    };
                })}
                multiple={true}
                mode='BADGE'
                setValue={setStatus}
                value={status}
                zIndex={4}
            />

            <CategoriesDropdown
                zIndex={3}
                value={categoryFilter}
                setValue={setCategoryFilter}
                multiple
                actingBodyId={getActingBodyId()}
            />

            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('createdAt')}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(2)}
                />
            </View>

            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(1)}
                />
            </View>

            <View style={{ flexDirection: 'column', gap: 8 }}>
                <FilterCheckBox
                    label={translate('overdueUnits')}
                    value={overdueUnits}
                    onChange={setOverdueUnits}
                />
                <FilterCheckBox
                    label={translate('liveStreamOnly')}
                    value={isConnected}
                    onChange={setIsConnected}
                />

                <FilterCheckBox
                    label={translate('unitsWithLocation')}
                    value={agentOnline}
                    onChange={setAgentOnline}
                    showTooltip
                    tooltipText={translate('onlyActiveUnitsWillBeShowWithThisFilter')}
                    zIndex={2}
                />

                <FilterCheckBox
                    label={translate('unitsWithoutLocation')}
                    value={agentOffline}
                    onChange={setAgentOffline}
                    showTooltip
                    tooltipText={translate('onlyActiveUnitsWillBeShowWithThisFilter')}
                    zIndex={1}
                />
            </View>
        </Filters>
    );
}


export default UnitsFilter;
